/**
 * Scenarios – AuditTrail
 */

import { AlertCircle, ArrowRight, Clock, GitBranch, Loader2, Plus, RefreshCw } from "lucide-react";
import {
  useGetScenarioAudit,
  getGetScenarioAuditQueryKey,
} from "@workspace/api-client-react";
import type { ScenarioAuditEvent } from "@workspace/api-client-react";
import { formatTs } from "./utils";

const EVENT_LABELS: Record<string, string> = {
  created: "Created",
  driver_added: "Driver Added",
  recomputed: "Recomputed",
  promoted: "Promoted to Working Forecast",
};

function EventIcon({ type }: { type: string }) {
  if (type === "promoted") return <ArrowRight size={11} />;
  if (type === "driver_added") return <Plus size={11} />;
  if (type === "recomputed") return <RefreshCw size={11} />;
  return <GitBranch size={11} />;
}

export function AuditTrail({ scenarioId }: { scenarioId: string }) {
  const { data: auditEvents, isLoading, isError } = useGetScenarioAudit(
    scenarioId,
    {
      query: {
        queryKey: getGetScenarioAuditQueryKey(scenarioId),
        enabled: !!scenarioId,
      },
    }
  );

  if (isLoading) {
    return (
      <div className="sc-audit-loading" data-testid="audit-loading">
        <Loader2 size={14} className="animate-spin" />
        <span>Loading audit log…</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="sc-form-error" role="alert" data-testid="audit-error">
        <AlertCircle size={13} />
        <span>Audit log unavailable.</span>
      </div>
    );
  }

  if (!auditEvents || auditEvents.length === 0) {
    return (
      <div className="sc-empty-state" data-testid="audit-empty">
        <Clock size={16} />
        <p>No audit events yet.</p>
      </div>
    );
  }

  return (
    <div className="sc-audit-list" data-testid="audit-list">
      {(auditEvents as ScenarioAuditEvent[]).map((event) => (
        <div
          className="sc-audit-event"
          key={event.auditId}
          data-testid={`audit-event-${event.auditId}`}
        >
          <div className="sc-audit-event__icon">
            <EventIcon type={event.eventType} />
          </div>
          <div className="sc-audit-event__body">
            <span className="sc-audit-event__type">
              {EVENT_LABELS[event.eventType] ?? event.eventType}
            </span>
            <span className="sc-audit-event__actor">{event.actor}</span>
            <span className="sc-audit-event__ts">
              {formatTs(event.occurredAt)}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}
